import { useMemo, type ReactNode } from 'react'

import { type ClientFlagKey } from '@bettermode/flags/registry'
import {
  FlagsSnapshotProvider,
  useClientFlag,
  useFlagsSnapshot as useSharedFlagsSnapshot,
  type FlagsSnapshot,
} from '@tribeplatform/react-components/flags'

/**
 * Client-side feature-flag snapshot.
 *
 * The browser never talks to PostHog directly (no `posthog-js`, honoring the
 * no-member-tracking contract). Instead the server resolves the client-consumed
 * flags during SSR — inside the per-request `AsyncLocalStorage` evaluation
 * context — and serializes them into the page via `__BM_DATA__`
 * (`InjectedAppProps.flags`). This provider exposes that immutable snapshot to
 * the React tree, and the per-flag `client/featureFlags/hooks/*` read it via
 * `useFlag`.
 *
 * The context itself lives in `@tribeplatform/react-components/flags` rather than
 * here, because shared components rendered out of that package have to read the
 * same snapshot and cannot import from `web`. This is the only place it gets
 * mounted.
 */
export type { FlagsSnapshot }

interface FlagsProviderProps {
  flags: FlagsSnapshot | undefined
  children: ReactNode
}

export const FlagsProvider = ({ flags, children }: FlagsProviderProps) => {
  const snapshot = useMemo<FlagsSnapshot>(() => flags ?? {}, [flags])
  return (
    <FlagsSnapshotProvider snapshot={snapshot}>
      {children}
    </FlagsSnapshotProvider>
  )
}

/**
 * The whole SSR-injected snapshot. Exists for the flag debug overlay, which
 * reports what the page was rendered with; application code should read a single
 * flag through `useFlag` so the `ClientFlagKey` type keeps it honest.
 */
export const useFlagsSnapshot = (): FlagsSnapshot => useSharedFlagsSnapshot()

/**
 * Read a `client`-tagged boolean flag from the SSR-injected snapshot. When the
 * key is absent (no PostHog env, or the flag was not injected) it falls back to
 * the flag's registry-wide `defaultValue` — baked in at codegen from its
 * `default-on` / `default-off` tag — so the default is never hand-coded at the
 * call site. `key` is a `ClientFlagKey`, so only flags delivered to the browser
 * can be read here.
 */
export const useFlag = (key: ClientFlagKey): boolean => useClientFlag(key)
